import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { commentsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FaThumbsUp, FaReply, FaEdit, FaTrash, FaTimes, FaCheck } from 'react-icons/fa';

const Comment = ({ comment, onReply, onUpdate, onDelete, isReply = false }) => {
  const { userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(comment.content);
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOwner = userProfile?.id === comment.user_id;

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Handle edit
  const handleEdit = async () => {
    if (!editedContent.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    setSubmitting(true);
    try {
      await commentsAPI.update(comment.id, { content: editedContent });
      toast.success('Comment updated!');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Edit window expired (15 min limit)');
      } else {
        toast.error('Failed to update comment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;

    setSubmitting(true);
    try {
      await commentsAPI.delete(comment.id);
      toast.success('Comment deleted');
      if (onDelete) onDelete();
    } catch (error) {
      toast.error('Failed to delete comment');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle helpful
  const handleHelpful = async () => {
    try {
      await commentsAPI.markHelpful(comment.id);
      toast.success('Marked as helpful!');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to mark as helpful');
    }
  };

  // Handle reply submit
  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) {
      toast.error('Reply cannot be empty');
      return;
    }

    if (onReply) {
      onReply(comment.id, replyContent);
      setReplyContent('');
      setIsReplying(false);
    }
  };

  return (
    <div className={`${isReply ? 'ml-8' : ''} mb-4`}>
      <div className="bg-white dark:bg-spooky-bg-medium rounded-lg p-4 shadow-sm">
        {/* Comment Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className={`level-badge level-${comment.level}`}>
              {comment.level}
            </span>
            <div>
              <span className="font-semibold text-gray-900 dark:text-spooky-ghost-white">
                {comment.display_name || comment.username}
              </span>
              <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">
                @{comment.username}
              </span>
              <span className="text-gray-400 dark:text-gray-500 text-sm ml-2">
                {formatDate(comment.created_at)}
              </span>
              {comment.updated_at && comment.updated_at !== comment.created_at && (
                <span className="text-gray-400 dark:text-gray-500 text-xs ml-2">(edited)</span>
              )}
            </div>
          </div>

          {/* Actions (owner only) */}
          {isOwner && !isEditing && (
            <div className="flex space-x-2">
              <button
                onClick={() => setIsEditing(true)}
                className="text-gray-400 hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition"
                title="Edit"
              >
                <FaEdit />
              </button>
              <button
                onClick={handleDelete}
                className="text-gray-400 hover:text-red-500 transition"
                title="Delete"
                disabled={submitting}
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>

        {/* Comment Content */}
        {isEditing ? (
          <div className="mb-3">
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-spooky-bg-dark dark:border-spooky-purple-mist dark:text-spooky-ghost-white focus:ring-2 focus:ring-boop-primary"
              rows="3"
              maxLength={2000}
            />
            <div className="flex justify-end space-x-2 mt-2">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedContent(comment.content);
                }}
                className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                disabled={submitting}
              >
                <FaTimes className="inline mr-1" /> Cancel
              </button>
              <button
                onClick={handleEdit}
                className="px-3 py-1 bg-boop-primary text-white rounded hover:bg-boop-secondary"
                disabled={submitting}
              >
                <FaCheck className="inline mr-1" /> {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-gray-700 dark:text-gray-300 mb-3 whitespace-pre-wrap">
            {comment.content}
          </p>
        )}

        {/* Comment Actions */}
        {!isEditing && (
          <div className="flex items-center space-x-4 text-sm">
            <button
              onClick={handleHelpful}
              className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition"
            >
              <FaThumbsUp />
              <span>{comment.helpful_count || 0} Helpful</span>
            </button>

            {!isReply && (
              <button
                onClick={() => setIsReplying(!isReplying)}
                className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition"
              >
                <FaReply />
                <span>Reply</span>
              </button>
            )}

            {comment.reply_count > 0 && !isReply && (
              <span className="text-gray-400 dark:text-gray-500 text-xs">
                {comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}
              </span>
            )}
          </div>
        )}

        {/* Reply Form */}
        {isReplying && (
          <form onSubmit={handleReplySubmit} className="mt-3 border-t dark:border-spooky-purple-mist pt-3">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              className="w-full px-3 py-2 border rounded-lg dark:bg-spooky-bg-dark dark:border-spooky-purple-mist dark:text-spooky-ghost-white focus:ring-2 focus:ring-boop-primary"
              rows="2"
              maxLength={2000}
            />
            <div className="flex justify-end space-x-2 mt-2">
              <button
                type="button"
                onClick={() => {
                  setIsReplying(false);
                  setReplyContent('');
                }}
                className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1 bg-boop-primary text-white rounded hover:bg-boop-secondary"
                disabled={submitting}
              >
                Reply
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-2">
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              onUpdate={onUpdate}
              onDelete={onDelete}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Comment;
